import { useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Globe, Server, ArrowLeft, RefreshCw, KeyRound, Lock, Unlock, Repeat, Mail } from 'lucide-react';
import {
  UniversalPage,
  UniversalLoadingState,
  StatusBadge,
  Card,
  NotificationCard,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  FormField,
  useToast,
  type StatusTone,
} from '@sighthost/ui';
import { useDomain, DnsEditor, type DomainStatus, type Domain } from '@/domains/domains-registry';
import {
  adminDomainService,
  type ContactPayload,
} from '@/domains/domains-registry/services/adminDomainService';
import { DomainsBalanceBanner } from '../../components/DomainsBalanceBanner';
import { formatDate } from '@/shared/utils/format';

const STATUS_LABEL: Record<DomainStatus, string> = {
  active: 'Active',
  expiring: 'Expiring',
  expired: 'Expired',
  pending: 'Pending',
  'transfer-in': 'Transfer in',
  'transfer-out': 'Transfer out',
  suspended: 'Suspended',
};

const STATUS_TONE: Record<DomainStatus, StatusTone> = {
  active: 'active',
  expiring: 'expiring',
  expired: 'overdue',
  pending: 'pending',
  'transfer-in': 'info',
  'transfer-out': 'info',
  suspended: 'failed',
};

/**
 * AdminDomainDetailPage — staff view of a single domain.
 *
 * Reuses the customer-side `useDomain` hook, which calls `domain-info`. The
 * EF allows staff role to read any customer's domain.
 *
 * Phase 8e.4 = read-only. Mutating actions (renew, transfer-out, lock,
 * contact edit, NS edit, auto-renew toggle) ship in 8e.6/7/9.
 */
export default function AdminDomainDetailPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { domain } = useParams();
  const decoded = domain ? decodeURIComponent(domain) : undefined;
  const { data, isLoading, error } = useDomain(decoded);
  const [busy, setBusy] = useState<string | null>(null);
  const [nsModal, setNsModal] = useState(false);
  const [contactsModal, setContactsModal] = useState(false);

  async function act(label: string, key: string, fn: () => Promise<unknown>) {
    if (!data) return;
    setBusy(key);
    try {
      const r = (await fn()) as { message?: string } | undefined;
      toast({ tone: 'success', title: label, body: r?.message });
    } catch (e) {
      toast({ tone: 'danger', title: `${label} failed`, body: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(null);
    }
  }

  const baseBreadcrumbs = [
    { label: 'Admin', href: '/admin' },
    { label: 'Domains', href: '/admin/domains' },
  ];

  if (isLoading) {
    return (
      <UniversalPage
        title={decoded ?? 'Domain'}
        breadcrumbs={[...baseBreadcrumbs, { label: decoded ?? 'Detail' }]}
      >
        <DomainsBalanceBanner />
        <UniversalLoadingState variant="spinner" caption="Loading registry data…" />
      </UniversalPage>
    );
  }

  if (error) {
    return (
      <UniversalPage
        title="Could not load domain"
        breadcrumbs={[...baseBreadcrumbs, { label: decoded ?? 'Detail' }]}
        actions={
          <Button variant="outline" size="sm" onClick={() => navigate('/admin/domains')}>
            <ArrowLeft className="h-4 w-4" /> Back to list
          </Button>
        }
      >
        <NotificationCard tone="danger" title="Registry error" body={error.message} />
      </UniversalPage>
    );
  }

  if (!data) {
    return (
      <UniversalPage
        title="Domain not found"
        breadcrumbs={[...baseBreadcrumbs, { label: decoded ?? 'Detail' }]}
        actions={
          <Button variant="outline" size="sm" onClick={() => navigate('/admin/domains')}>
            <ArrowLeft className="h-4 w-4" /> Back to list
          </Button>
        }
      >
        <Card interactive={false} className="p-12 text-center text-sm text-muted-foreground">
          No domain link exists for{' '}
          <code className="rounded-sm bg-muted px-1">{decoded}</code>.
        </Card>
      </UniversalPage>
    );
  }

  return (
    <UniversalPage
      title={
        <span className="inline-flex items-center gap-2">
          <Globe className="h-5 w-5 text-muted-foreground" />
          {data.name}
        </span>
      }
      breadcrumbs={[...baseBreadcrumbs, { label: data.name }]}
      meta={
        <>
          <StatusBadge tone={STATUS_TONE[data.status]}>{STATUS_LABEL[data.status]}</StatusBadge>
          <span className="text-muted-foreground">·</span>
          <span>Expires {formatDate(data.expiresAt)}</span>
          <span className="text-muted-foreground">·</span>
          <span>
            Auto-renew:{' '}
            <strong className="font-medium text-foreground">{data.autoRenew ? 'On' : 'Off'}</strong>
          </span>
          <span className="text-muted-foreground">·</span>
          <span>
            Lock:{' '}
            <strong className="font-medium text-foreground">
              {data.registryLocked ? 'Locked' : 'Unlocked'}
            </strong>
          </span>
        </>
      }
      actions={
        <Button variant="outline" size="sm" onClick={() => navigate('/admin/domains')}>
          <ArrowLeft className="h-4 w-4" /> Back to list
        </Button>
      }
    >
      <DomainsBalanceBanner />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <SectionCard title="Registration">
            <Field label="Domain" value={data.name} mono />
            <Field label="TLD" value={`.${data.tldFamily}`} />
            <Field label="Registered" value={formatDate(data.registeredAt)} />
            <Field
              label="Expires"
              value={
                <span>
                  {formatDate(data.expiresAt)} ({data.daysUntilExpiry}d)
                </span>
              }
            />
            <Field label="Registrar" value="domains.co.za" />
          </SectionCard>

          <SectionCard title="Nameservers">
            {data.nameservers.length === 0 ? (
              <p className="text-sm text-muted-foreground">None configured.</p>
            ) : (
              <ul className="space-y-1 text-sm">
                {data.nameservers.map((ns, i) => (
                  <li key={ns} className="flex items-center gap-2">
                    <StatusBadge tone="info">NS{i + 1}</StatusBadge>
                    <Server className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-xs tabular-nums">{ns}</span>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>

          {data.registrant && (
            <SectionCard title="Registrant">
              <Field label="Name" value={data.registrant.fullName} />
              {data.registrant.organisation && (
                <Field label="Organisation" value={data.registrant.organisation} />
              )}
              <Field label="Email" value={data.registrant.email} />
              <Field label="Phone" value={data.registrant.phone} />
              <Field
                label="Address"
                value={[
                  data.registrant.addressLine1,
                  data.registrant.city,
                  data.registrant.region,
                  data.registrant.postalCode,
                  data.registrant.countryCode,
                ]
                  .filter(Boolean)
                  .join(', ')}
              />
            </SectionCard>
          )}
        </div>

        <div className="space-y-6">
          <SectionCard title="Actions">
            <div className="space-y-2">
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                disabled={busy !== null}
                loading={busy === 'renew'}
                onClick={() =>
                  act('Renewal invoice issued', 'renew', () => adminDomainService.renew(data.id, 1))
                }
              >
                <RefreshCw className="h-4 w-4" />
                Issue 1-year renewal invoice
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                disabled={busy !== null}
                loading={busy === 'autorenew'}
                onClick={() =>
                  act(
                    data.autoRenew ? 'Auto-renew disabled' : 'Auto-renew enabled',
                    'autorenew',
                    () => adminDomainService.toggleAutoRenew(data.id, !data.autoRenew),
                  )
                }
              >
                <Repeat className="h-4 w-4" />
                {data.autoRenew ? 'Disable auto-renew' : 'Enable auto-renew'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                disabled={busy !== null}
                loading={busy === 'lock'}
                onClick={() =>
                  act(
                    data.registryLocked ? 'Unlocked' : 'Locked',
                    'lock',
                    () =>
                      adminDomainService.setLock(data.id, data.registryLocked ? 'unlock' : 'lock'),
                  )
                }
              >
                {data.registryLocked ? <Unlock className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                {data.registryLocked ? 'Unlock at registry' : 'Lock at registry'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                disabled={busy !== null}
                loading={busy === 'epp'}
                onClick={() =>
                  act('EPP code emailed', 'epp', () => adminDomainService.sendEppCode(data.id))
                }
              >
                <Mail className="h-4 w-4" />
                Email EPP code to registrant
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                disabled={busy !== null}
                onClick={() => setNsModal(true)}
              >
                <Server className="h-4 w-4" />
                Update nameservers
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                disabled={busy !== null || !data.registrant}
                onClick={() => setContactsModal(true)}
                title={!data.registrant ? 'Registrant data not loaded; refresh first' : undefined}
              >
                <KeyRound className="h-4 w-4" />
                Update registrant contact
              </Button>
            </div>
            <p className="mt-4 text-xs text-muted-foreground">
              All actions hit live registrar EFs (idempotency-keyed, audit-logged). Changes can take
              minutes to propagate — refresh the list page to re-sync.
            </p>
          </SectionCard>
        </div>
      </div>

      {/* DNS records (G1) — admin can edit anything including apex NS */}
      <div className="mt-6">
        <DnsEditor domain={data.name} mode="admin" />
      </div>

      <NsEditModal
        open={nsModal}
        onClose={() => setNsModal(false)}
        domain={data}
        onSave={async (ns) => {
          await act('Nameservers updated', 'ns', () =>
            adminDomainService.updateNameservers(data.id, ns),
          );
          setNsModal(false);
        }}
        saving={busy === 'ns'}
      />

      <ContactsEditModal
        open={contactsModal}
        onClose={() => setContactsModal(false)}
        domain={data}
        onSave={async (contact) => {
          await act('Registrant updated', 'contacts', () =>
            adminDomainService.updateContacts(data.id, { registrant: contact }),
          );
          setContactsModal(false);
        }}
        saving={busy === 'contacts'}
      />
    </UniversalPage>
  );
}

// Canonical default NS per CLAUDE.md §4.9 + system_setting.domains_default_nameservers
// (updated 2026-06-02 to cpns.host/cpns.zone). cPanel Cloud DNS Anycast cluster.
const SIGHTHOST_DEFAULT_NS = ['ns1.cpns.host', 'ns2.cpns.zone', 'ns3.cpns.host', 'ns4.cpns.zone'];

function NsEditModal({
  open,
  onClose,
  domain,
  onSave,
  saving,
}: {
  open: boolean;
  onClose: () => void;
  domain: Domain;
  onSave: (ns: string[]) => Promise<void>;
  saving: boolean;
}) {
  const initial = (domain.nameservers ?? []).slice(0, 5);
  while (initial.length < 5) initial.push('');
  const [ns, setNs] = useState<string[]>(initial);

  function update(i: number, v: string) {
    const next = [...ns];
    next[i] = v;
    setNs(next);
  }

  function reset() {
    const next = [...SIGHTHOST_DEFAULT_NS, '', '', '', '', ''].slice(0, 5);
    setNs(next);
  }

  async function submit() {
    const cleaned = ns.map((s) => s.trim().toLowerCase()).filter(Boolean);
    if (cleaned.length === 0) return;
    await onSave(cleaned);
  }

  return (
    <Modal
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title={`Update nameservers · ${domain.name}`}
      description="Provide 1–5 nameservers. Empty rows are ignored."
      size="wide"
    >
      <ModalBody>
        <div className="space-y-3">
          {ns.map((v, i) => (
            <FormField key={i} label={`NS${i + 1}`} htmlFor={`ns-${i}`}>
              <Input
                id={`ns-${i}`}
                value={v}
                onChange={(e) => update(i, e.target.value)}
                placeholder={SIGHTHOST_DEFAULT_NS[i] ?? 'optional'}
                disabled={saving}
                spellCheck={false}
                autoCapitalize="off"
                autoCorrect="off"
                className="text-sm tabular-nums"
              />
            </FormField>
          ))}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="outline" onClick={reset} disabled={saving}>
          Reset to SightHost defaults
        </Button>
        <Button variant="outline" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={submit} loading={saving} disabled={saving}>
          Save nameservers
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function ContactsEditModal({
  open,
  onClose,
  domain,
  onSave,
  saving,
}: {
  open: boolean;
  onClose: () => void;
  domain: Domain;
  onSave: (contact: ContactPayload) => Promise<void>;
  saving: boolean;
}) {
  const r = domain.registrant;
  const [form, setForm] = useState<ContactPayload>({
    name: r?.fullName ?? '',
    email: r?.email ?? '',
    contactNumber: r?.phone ?? '',
    organisation: r?.organisation ?? '',
    address1: r?.addressLine1 ?? '',
    city: r?.city ?? '',
    province: r?.region ?? '',
    postalCode: r?.postalCode ?? '',
    country: r?.countryCode ?? 'ZA',
  });

  function set<K extends keyof ContactPayload>(k: K, v: string) {
    setForm((prev) => ({ ...prev, [k]: v }));
  }

  return (
    <Modal
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title={`Update registrant · ${domain.name}`}
      description="ZACR may trigger a verification email after changes. Only fields you edit are sent."
      size="wide"
    >
      <ModalBody>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FormField label="Full name" htmlFor="c-name">
            <Input id="c-name" value={form.name ?? ''} onChange={(e) => set('name', e.target.value)} disabled={saving} />
          </FormField>
          <FormField label="Email" htmlFor="c-email">
            <Input id="c-email" type="email" value={form.email ?? ''} onChange={(e) => set('email', e.target.value)} disabled={saving} />
          </FormField>
          <FormField label="Phone (e.g. +27.123456789)" htmlFor="c-phone">
            <Input id="c-phone" value={form.contactNumber ?? ''} onChange={(e) => set('contactNumber', e.target.value)} disabled={saving} />
          </FormField>
          <FormField label="Organisation (optional)" htmlFor="c-org">
            <Input id="c-org" value={form.organisation ?? ''} onChange={(e) => set('organisation', e.target.value)} disabled={saving} />
          </FormField>
          <FormField label="Address line 1" htmlFor="c-addr">
            <Input id="c-addr" value={form.address1 ?? ''} onChange={(e) => set('address1', e.target.value)} disabled={saving} />
          </FormField>
          <FormField label="City" htmlFor="c-city">
            <Input id="c-city" value={form.city ?? ''} onChange={(e) => set('city', e.target.value)} disabled={saving} />
          </FormField>
          <FormField label="Province" htmlFor="c-prov">
            <Input id="c-prov" value={form.province ?? ''} onChange={(e) => set('province', e.target.value)} disabled={saving} />
          </FormField>
          <FormField label="Postal code" htmlFor="c-pc">
            <Input id="c-pc" value={form.postalCode ?? ''} onChange={(e) => set('postalCode', e.target.value)} disabled={saving} />
          </FormField>
          <FormField label="Country (ISO 3166-1 alpha-2)" htmlFor="c-country">
            <Input id="c-country" maxLength={2} value={form.country ?? ''} onChange={(e) => set('country', e.target.value.toUpperCase())} disabled={saving} />
          </FormField>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="outline" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={() => onSave(form)} loading={saving} disabled={saving}>
          Save registrant
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card interactive={false}>
      <h3 className="border-b border-border px-6 py-3 text-sm font-semibold text-ink">
        {title}
      </h3>
      <div className="space-y-3 px-6 py-5">{children}</div>
    </Card>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="grid grid-cols-3 gap-4 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={`col-span-2 text-foreground ${mono ? 'text-xs tabular-nums' : ''}`}>
        {value}
      </dd>
    </div>
  );
}
